"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  FiBookmark,
  FiCheckSquare,
  FiHome,
  FiPackage,
  FiSearch,
  FiShoppingCart,
  FiTruck,
  FiUserCheck,
  FiX,
} from "react-icons/fi";
import { MdOutlineDashboard } from "react-icons/md";

type MobileNavDrawerProps = {
  isAdmin: boolean;
  isOpen: boolean;
  onClose: () => void;
};

const MobileNavDrawer = ({
  isAdmin,
  isOpen,
  onClose,
}: MobileNavDrawerProps) => {
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  const isActivePath = (path: string) =>
    pathname === path || pathname?.startsWith(`${path}/`);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const enterTimeoutId = window.setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);

      return () => window.clearTimeout(enterTimeoutId);
    }

    setIsAnimatingIn(false);
    const timeoutId = window.setTimeout(() => {
      setIsVisible(false);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-50 lg:hidden"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close navigation drawer"
        className={`absolute inset-0 bg-slate-900/50 transition-opacity duration-300 ease-out ${
          isAnimatingIn ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      <aside
        className={`absolute left-0 top-0 h-full w-72 border-r border-primary-100 bg-white shadow-xl transition-transform duration-300 ease-out will-change-transform ${
          isAnimatingIn ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-primary-100 shadow-md px-4 py-4">
          <Image
            src="/images/app/logo-with-title.png"
            alt="ReEquip Logo"
            width={100}
            height={80}
            priority
          />
          <button
            type="button"
            aria-label="Close menu"
            className="rounded-md p-1 cursor-pointer text-slate-700 transition hover:bg-primary-50 hover:text-primary-700"
            onClick={onClose}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-1 p-3">
          {isAdmin ? (
            <>
              <Link
                href="/admin/home"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/admin/home")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <MdOutlineDashboard className="h-4 w-4" />
                Home
              </Link>
              <Link
                href="/admin/user-details"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/admin/user-details")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiUserCheck className="h-4 w-4" />
                User Details
              </Link>
              <Link
                href="/admin/equipment-verification"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/admin/equipment-verification")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiCheckSquare className="h-4 w-4" />
                Equipment Verification
              </Link>
              <Link
                href="/admin/rental-requests"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/admin/rental-requests") ||
                  isActivePath("/admin/rental-request")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiShoppingCart className="h-4 w-4" />
                Rental Requests
              </Link>

              <div className="my-2 border-t border-slate-200" />
              <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                User Links
              </p>

              <Link
                href="/home"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/home")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiHome className="h-4 w-4" />
                Home
              </Link>
              <Link
                href="/search-equipment"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/search-equipment")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiSearch className="h-4 w-4" />
                Search Equipment
              </Link>
              <Link
                href="/my-listings"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-listings")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiPackage className="h-4 w-4" />
                My Listings
              </Link>
              <Link
                href="/my-rental-requests"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-rental-requests")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiTruck className="h-4 w-4" />
                My Rental Requests
              </Link>
              <Link
                href="/my-wishlist"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-wishlist")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiBookmark className="h-4 w-4" />
                My Wishlist
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/home"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/home")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiHome className="h-4 w-4" />
                Home
              </Link>
              <Link
                href="/search-equipment"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/search-equipment")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiSearch className="h-4 w-4" />
                Search Equipment
              </Link>
              <Link
                href="/my-listings"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-listings")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiPackage className="h-4 w-4" />
                My Listings
              </Link>
              <Link
                href="/my-rental-requests"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-rental-requests")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiTruck className="h-4 w-4" />
                My Rental Requests
              </Link>
              <Link
                href="/my-wishlist"
                className={`inline-flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActivePath("/my-wishlist")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-primary-50 hover:text-blue-600"
                }`}
                onClick={onClose}
              >
                <FiBookmark className="h-4 w-4" />
                My Wishlist
              </Link>
            </>
          )}
        </nav>
      </aside>
    </div>
  );
};

export default MobileNavDrawer;
